/**
Copyright (c) 2022–2026 Podstack.ai
All rights reserved.
Proprietary and Confidential.
Unauthorized copying of this file, via any medium, is strictly prohibited.
This file is part of the Podstack Platform.
*/

// GPU Pods → Templates tab (/pods/templates). Saved GPU, image and storage
// setups; launch one in a click, edit, duplicate or delete.
// ?edit=<templateId> opens the edit dialog (used by the Pods quick-start cards).

import React, { useEffect, useMemo, useState } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { templateService, containerService } from '../services/api';
import toast from 'react-hot-toast';
import { format } from 'date-fns';
import { Container, LaunchTemplate, Project } from '../types';
import { useSelectedProject } from '../hooks/useSelectedProject';
import { usePriceUnit } from '../hooks/usePriceUnit';
import { Page, Button, EmptyState, Skeleton, Tag, KV } from '../components/console/ui';
import { CloseIc, LayersIc, PlusIc } from '../components/console/icons';
import PodsTabs from '../components/pods/PodsTabs';
import Dialog from '../components/pods/Dialog';
import { shortImage, useGpuCatalog, useSavedTemplates, useSellPrice } from '../components/pods/launch/catalog';

interface EditForm {
  name: string;
  description: string;
  image_name: string;
  cpu_cores: number;
  memory_gb: number;
  gpu_type: string;
  gpu_cores: number;
  ports: number[];
  env_variables: Record<string, string>;
  init_command: string;
  tags: string[];
}

const EMPTY_EDIT: EditForm = {
  name: '',
  description: '',
  image_name: '',
  cpu_cores: 0,
  memory_gb: 0,
  gpu_type: '',
  gpu_cores: 0,
  ports: [],
  env_variables: {},
  init_command: '',
  tags: [],
};

const Templates: React.FC = () => {
  const navigate = useNavigate();
  const { perMinute } = usePriceUnit();
  const queryClient = useQueryClient();
  const [searchParams, setSearchParams] = useSearchParams();
  const { selectedProjectId, projects, projectsLoading: loadingProjects } = useSelectedProject();

  const [templateToDelete, setTemplateToDelete] = useState<LaunchTemplate | null>(null);
  const [editingTemplate, setEditingTemplate] = useState<LaunchTemplate | null>(null);
  const [editFormData, setEditFormData] = useState<EditForm>(EMPTY_EDIT);
  const [newPort, setNewPort] = useState('');
  const [newEnvKey, setNewEnvKey] = useState('');
  const [newEnvValue, setNewEnvValue] = useState('');
  const [newTag, setNewTag] = useState('');

  // User's custom templates (project-scoped when a project is selected)
  const { templates: customTemplates, isLoading: loadingTemplates } = useSavedTemplates(selectedProjectId || undefined);

  // Tab badge: running pods in this project
  const { data: containersData } = useQuery({
    queryKey: ['containers', selectedProjectId],
    queryFn: () => containerService.list(selectedProjectId || undefined).then((res) => res.data),
  });
  const runningCount = useMemo(() => {
    const list: Container[] = Array.isArray(containersData)
      ? containersData
      : containersData?.data || containersData?.containers || [];
    return list.filter((c) => c.status?.toLowerCase() === 'running').length;
  }, [containersData]);

  // GPU VRAM + est. price per template
  const { gpus } = useGpuCatalog();
  const { foldSellPrice } = useSellPrice();
  const gpuFor = (t: LaunchTemplate) => gpus.find((g) => g.gpu_type === t.gpu_type);
  const projectName = (id?: string) => (projects as Project[]).find((p) => p.id === id)?.name;

  const deleteMutation = useMutation({
    mutationFn: (id: string) => templateService.delete(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['my-templates'] });
      toast.success('Template deleted successfully');
      setTemplateToDelete(null);
    },
    onError: (error: any) => {
      toast.error(error.response?.data?.error || 'Failed to delete template');
    },
  });

  const updateMutation = useMutation({
    mutationFn: ({ id, projectId, data }: { id: string; projectId?: string; data: any }) =>
      templateService.update(id, projectId, data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['my-templates'] });
      toast.success('Template updated successfully');
      closeEdit();
    },
    onError: (error: any) => {
      toast.error(error.response?.data?.error || 'Failed to update template');
    },
  });

  const duplicateMutation = useMutation({
    mutationFn: (t: LaunchTemplate) =>
      templateService.create({
        name: `${t.name} (copy)`,
        description: t.description || '',
        type: t.type || 'pod',
        scope: 'custom',
        project_id: t.project_id || selectedProjectId || undefined,
        image_name: t.image_name,
        cpu_cores: t.cpu_cores,
        memory_gb: t.memory_gb,
        storage_gb: t.storage_gb,
        ...(t.gpu_type ? { gpu_type: t.gpu_type, gpu_cores: t.gpu_cores } : {}),
        env_variables: t.env_variables,
        ports: t.ports,
        init_command: t.init_command,
        tags: t.tags,
      }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['my-templates'] });
      toast.success('Template duplicated');
    },
    onError: (error: any) => {
      toast.error(error.response?.data?.error || 'Failed to duplicate template');
    },
  });

  const handleEdit = (template: LaunchTemplate) => {
    setEditingTemplate(template);
    setEditFormData({
      name: template.name,
      description: template.description || '',
      image_name: template.image_name,
      cpu_cores: template.cpu_cores || 0,
      memory_gb: template.memory_gb || 0,
      gpu_type: template.gpu_type || '',
      gpu_cores: template.gpu_cores || 0,
      ports: template.ports || [],
      env_variables: template.env_variables || {},
      init_command: template.init_command || '',
      tags: template.tags || [],
    });
  };

  // Deep link: /pods/templates?edit=<id>
  const editParam = searchParams.get('edit');
  useEffect(() => {
    if (!editParam || customTemplates.length === 0) return;
    const t = customTemplates.find((x) => x.id === editParam);
    if (t) handleEdit(t);
    const next = new URLSearchParams(searchParams);
    next.delete('edit');
    setSearchParams(next, { replace: true });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [editParam, customTemplates]);

  const closeEdit = () => {
    setEditingTemplate(null);
    setNewPort('');
    setNewEnvKey('');
    setNewEnvValue('');
    setNewTag('');
  };

  const handleUpdateTemplate = (e: React.FormEvent) => {
    e.preventDefault();
    if (!editingTemplate) return;
    updateMutation.mutate({
      id: editingTemplate.id,
      projectId: editingTemplate.project_id,
      data: {
        name: editFormData.name,
        description: editFormData.description,
        image: editFormData.image_name,
        cpu_cores: editFormData.cpu_cores,
        memory_gb: editFormData.memory_gb,
        gpu_type: editFormData.gpu_type,
        gpu_count: editFormData.gpu_cores,
        ports: editFormData.ports,
        env_vars: editFormData.env_variables,
        startup_command: editFormData.init_command,
      },
    });
  };

  const handleLaunchPod = (template: LaunchTemplate) => {
    navigate('/pods/create', { state: { template, preselectedGpu: template.gpu_type } });
  };

  const handleCreateTemplate = () => navigate('/pods/create');

  const addPort = () => {
    const port = parseInt(newPort, 10);
    if (port && !editFormData.ports.includes(port)) {
      setEditFormData({ ...editFormData, ports: [...editFormData.ports, port] });
      setNewPort('');
    }
  };
  const removePort = (port: number) =>
    setEditFormData({ ...editFormData, ports: editFormData.ports.filter((p) => p !== port) });
  const addEnvVar = () => {
    if (newEnvKey && !editFormData.env_variables[newEnvKey]) {
      setEditFormData({ ...editFormData, env_variables: { ...editFormData.env_variables, [newEnvKey]: newEnvValue } });
      setNewEnvKey('');
      setNewEnvValue('');
    }
  };
  const removeEnvVar = (key: string) => {
    const { [key]: _removed, ...rest } = editFormData.env_variables;
    setEditFormData({ ...editFormData, env_variables: rest });
  };
  const addTag = () => {
    if (newTag && !editFormData.tags.includes(newTag)) {
      setEditFormData({ ...editFormData, tags: [...editFormData.tags, newTag] });
      setNewTag('');
    }
  };
  const removeTag = (tag: string) => setEditFormData({ ...editFormData, tags: editFormData.tags.filter((t) => t !== tag) });

  const isLoading = loadingProjects || loadingTemplates;

  const pill = (label: React.ReactNode, onRemove: () => void, key: React.Key) => (
    <span key={key} className="inline-flex items-center gap-1 pl-3 pr-1 min-h-[32px] rounded-full bg-ps-raised text-sm">
      {label}
      <button
        type="button"
        onClick={onRemove}
        aria-label="Remove"
        className="w-7 h-7 grid place-items-center rounded-full text-ps-subtle hover:text-ps-err"
      >
        <CloseIc className="w-3.5 h-3.5" />
      </button>
    </span>
  );

  return (
    <Page>
      <div className="flex flex-wrap justify-between items-end gap-4">
        <div>
          <div className="ps-eyebrow">QuickPods</div>
          <h1 className="mt-1 mb-0 text-2xl sm:text-[28px] font-medium text-ps-text">GPU Pods</h1>
        </div>
        <Button onClick={() => navigate('/pods/create')}>
          <PlusIc className="w-4 h-4" />
          Launch pod
        </Button>
      </div>

      <PodsTabs active="templates" runningCount={runningCount} templateCount={isLoading ? undefined : customTemplates.length} />

      <div className="flex flex-wrap justify-between items-center gap-3">
        <p className="m-0 text-sm text-ps-muted">Saved GPU, image and storage setups. Launch one in a single click.</p>
        <Button variant="secondary" onClick={handleCreateTemplate}>
          New template
        </Button>
      </div>

      {isLoading ? (
        <div className="grid gap-4 grid-cols-[repeat(auto-fill,minmax(min(100%,300px),1fr))]">
          {Array.from({ length: 3 }).map((_, i) => (
            <Skeleton key={i} className="h-[300px]" />
          ))}
        </div>
      ) : customTemplates.length === 0 ? (
        <EmptyState
          icon={<LayersIc />}
          title="No saved templates yet"
          body="Configure a pod once, then tick “Save as template” on the last step of the launch flow to reuse it here."
          actions={<Button onClick={handleCreateTemplate}>Launch a pod</Button>}
        />
      ) : (
        <section className="grid gap-4 grid-cols-[repeat(auto-fill,minmax(min(100%,300px),1fr))]" aria-label="Templates">
          {customTemplates.map((template) => {
            const gpu = gpuFor(template);
            const gpuShare = template.gpu_cores && template.gpu_cores > 0 && template.gpu_cores <= 1 ? template.gpu_cores : 1;
            return (
              <article key={template.id} className="ps-card p-5 flex flex-col gap-3">
                <div className="flex justify-between items-start gap-2">
                  <div className="min-w-0">
                    <h2 className="m-0 text-[17px] font-medium truncate">{template.name}</h2>
                    <div className="text-[13px] text-ps-subtle mt-0.5 line-clamp-2">
                      {template.description || (
                        <button type="button" className="hover:text-ps-muted" onClick={() => handleEdit(template)}>
                          Add a description
                        </button>
                      )}
                    </div>
                  </div>
                  <Tag tone="info">{template.type === 'vm' ? 'VM' : 'Pod'}</Tag>
                </div>
                <div className="flex flex-col gap-2 border-t border-ps-border pt-3">
                  <KV k="Image" v={<span className="font-mono" title={template.image_name}>{shortImage(template.image_name)}</span>} />
                  <KV k="GPU" v={template.gpu_type ? `${template.gpu_type}${gpu ? ` · ${gpu.memory_gb} GB` : ''}` : 'None'} />
                  <KV
                    k="CPU / RAM"
                    v={`${template.cpu_cores > 0 ? `${template.cpu_cores} vCPU` : '-'} · ${template.memory_gb > 0 ? `${template.memory_gb} GB` : '-'}`}
                  />
                  {projectName(template.project_id) && <KV k="Project" v={projectName(template.project_id)} />}
                  <KV k="Saved" v={format(new Date(template.created_at), 'd MMM yyyy')} />
                  {gpu && (
                    <KV
                      k="Est. price"
                      v={
                        perMinute
                          ? `₹${(foldSellPrice(gpu.price_per_minute) * gpuShare).toFixed(2)}/min · ₹${(foldSellPrice(gpu.price_per_minute) * 60 * gpuShare).toFixed(0)}/hr`
                          : `₹${(foldSellPrice(gpu.price_per_minute) * 60 * gpuShare).toFixed(0)}/hr`
                      }
                    />
                  )}
                </div>
                {template.tags && template.tags.length > 0 && (
                  <div className="flex flex-wrap gap-1">
                    {template.tags.slice(0, 3).map((tag) => (
                      <Tag key={tag} tone="off">
                        {tag}
                      </Tag>
                    ))}
                    {template.tags.length > 3 && <span className="text-xs text-ps-subtle">+{template.tags.length - 3} more</span>}
                  </div>
                )}
                <div className="flex gap-2 flex-wrap mt-auto">
                  <button type="button" className="ps-btn ps-btn-sm max-[820px]:min-h-[44px]" onClick={() => handleLaunchPod(template)}>
                    Launch
                  </button>
                  <button type="button" className="ps-btn2 ps-btn-sm max-[820px]:min-h-[44px]" onClick={() => handleEdit(template)}>
                    Edit
                  </button>
                  <button
                    type="button"
                    className="ps-btn2 ps-btn-sm max-[820px]:min-h-[44px]"
                    onClick={() => duplicateMutation.mutate(template)}
                    disabled={duplicateMutation.isPending && duplicateMutation.variables?.id === template.id}
                  >
                    Duplicate
                  </button>
                  <button
                    type="button"
                    className="ps-btn-danger ps-btn-sm max-[820px]:min-h-[44px] ml-auto"
                    onClick={() => setTemplateToDelete(template)}
                  >
                    Delete
                  </button>
                </div>
              </article>
            );
          })}
          <Link
            to="/pods/create"
            className="ps-card border-dashed p-5 flex flex-col gap-2 justify-center items-center text-center min-h-[260px] text-ps-text hover:border-ps-strong"
          >
            <PlusIc className="w-7 h-7 text-ps-accent" />
            <span className="font-semibold">Create a template</span>
            <span className="text-[13px] text-ps-muted max-w-[240px]">Or use “Save as template” on the last step of any launch.</span>
          </Link>
        </section>
      )}

      {/* Delete confirmation */}
      <Dialog
        open={!!templateToDelete}
        onClose={() => setTemplateToDelete(null)}
        title="Delete template"
        footer={
          <>
            <button type="button" className="ps-btn2" onClick={() => setTemplateToDelete(null)}>
              Cancel
            </button>
            <button
              type="button"
              className="ps-btn-danger"
              onClick={() => templateToDelete && deleteMutation.mutate(templateToDelete.id)}
              disabled={deleteMutation.isPending}
            >
              {deleteMutation.isPending ? 'Deleting…' : 'Delete'}
            </button>
          </>
        }
      >
        <p className="m-0 text-sm text-ps-muted">
          Are you sure you want to delete the template{' '}
          <span className="font-semibold text-ps-text">“{templateToDelete?.name}”</span>? This action cannot be undone.
        </p>
      </Dialog>

      {/* Edit template */}
      <Dialog
        open={!!editingTemplate}
        onClose={closeEdit}
        title="Edit template"
        size="lg"
        footer={
          <>
            <button type="button" className="ps-btn2" onClick={closeEdit}>
              Cancel
            </button>
            <button type="submit" form="edit-template" className="ps-btn" disabled={updateMutation.isPending}>
              {updateMutation.isPending ? 'Saving…' : 'Save changes'}
            </button>
          </>
        }
      >
        <form id="edit-template" onSubmit={handleUpdateTemplate} className="flex flex-col gap-4">
          <label className="block">
            <span className="ps-label">Template name</span>
            <input
              type="text"
              className="ps-input"
              required
              value={editFormData.name}
              onChange={(e) => setEditFormData({ ...editFormData, name: e.target.value })}
            />
          </label>
          <label className="block">
            <span className="ps-label">Description</span>
            <textarea
              className="ps-input py-2 resize-none"
              rows={2}
              value={editFormData.description}
              onChange={(e) => setEditFormData({ ...editFormData, description: e.target.value })}
            />
          </label>
          <label className="block">
            <span className="ps-label">Docker image</span>
            <input
              type="text"
              className="ps-input font-mono"
              required
              placeholder="e.g., pytorch/pytorch:latest"
              value={editFormData.image_name}
              onChange={(e) => setEditFormData({ ...editFormData, image_name: e.target.value })}
            />
          </label>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <label className="block">
              <span className="ps-label">CPU cores</span>
              <input
                type="number"
                min="0"
                className="ps-input"
                value={editFormData.cpu_cores}
                onChange={(e) => setEditFormData({ ...editFormData, cpu_cores: parseInt(e.target.value, 10) || 0 })}
              />
            </label>
            <label className="block">
              <span className="ps-label">Memory (GB)</span>
              <input
                type="number"
                min="0"
                className="ps-input"
                value={editFormData.memory_gb}
                onChange={(e) => setEditFormData({ ...editFormData, memory_gb: parseInt(e.target.value, 10) || 0 })}
              />
            </label>
            <label className="block">
              <span className="ps-label">GPU type</span>
              <input
                type="text"
                className="ps-input"
                placeholder="e.g., NVIDIA RTX 4090"
                value={editFormData.gpu_type}
                onChange={(e) => setEditFormData({ ...editFormData, gpu_type: e.target.value })}
              />
            </label>
            <label className="block">
              <span className="ps-label">GPU count</span>
              <input
                type="number"
                min="0"
                className="ps-input"
                value={editFormData.gpu_cores}
                onChange={(e) => setEditFormData({ ...editFormData, gpu_cores: parseInt(e.target.value, 10) || 0 })}
              />
            </label>
          </div>

          <div>
            <span className="ps-label">Exposed ports</span>
            <div className="flex gap-2 mb-2">
              <input
                type="number"
                className="ps-input flex-1"
                placeholder="Port number"
                aria-label="Port number"
                value={newPort}
                onChange={(e) => setNewPort(e.target.value)}
                onKeyDown={(e) => e.key === 'Enter' && (e.preventDefault(), addPort())}
              />
              <button type="button" onClick={addPort} className="ps-btn2">
                Add
              </button>
            </div>
            <div className="flex flex-wrap gap-2">{editFormData.ports.map((port) => pill(port, () => removePort(port), port))}</div>
          </div>

          <div>
            <span className="ps-label">Environment variables</span>
            <div className="flex flex-wrap gap-2 mb-2">
              <input
                type="text"
                className="ps-input font-mono flex-[1_1_140px]"
                placeholder="Key"
                aria-label="Variable name"
                value={newEnvKey}
                onChange={(e) => setNewEnvKey(e.target.value)}
              />
              <input
                type="text"
                className="ps-input font-mono flex-[2_1_200px]"
                placeholder="Value"
                aria-label="Variable value"
                value={newEnvValue}
                onChange={(e) => setNewEnvValue(e.target.value)}
                onKeyDown={(e) => e.key === 'Enter' && (e.preventDefault(), addEnvVar())}
              />
              <button type="button" onClick={addEnvVar} className="ps-btn2">
                Add
              </button>
            </div>
            <div className="flex flex-col gap-1">
              {Object.entries(editFormData.env_variables).map(([key, value]) => (
                <div key={key} className="flex items-center justify-between gap-2 px-3 min-h-[36px] rounded-md bg-ps-raised text-sm">
                  <span className="font-mono truncate">
                    <span className="font-semibold">{key}</span> = {value}
                  </span>
                  <button
                    type="button"
                    onClick={() => removeEnvVar(key)}
                    aria-label={`Remove ${key}`}
                    className="w-7 h-7 grid place-items-center rounded text-ps-subtle hover:text-ps-err"
                  >
                    <CloseIc className="w-3.5 h-3.5" />
                  </button>
                </div>
              ))}
            </div>
          </div>

          <label className="block">
            <span className="ps-label">Startup command</span>
            <input
              type="text"
              className="ps-input font-mono"
              placeholder="e.g., python main.py"
              value={editFormData.init_command}
              onChange={(e) => setEditFormData({ ...editFormData, init_command: e.target.value })}
            />
          </label>

          <div>
            <span className="ps-label">Tags</span>
            <div className="flex gap-2 mb-2">
              <input
                type="text"
                className="ps-input flex-1"
                placeholder="Add a tag"
                aria-label="Tag"
                value={newTag}
                onChange={(e) => setNewTag(e.target.value)}
                onKeyDown={(e) => e.key === 'Enter' && (e.preventDefault(), addTag())}
              />
              <button type="button" onClick={addTag} className="ps-btn2">
                Add
              </button>
            </div>
            <div className="flex flex-wrap gap-2">{editFormData.tags.map((tag) => pill(tag, () => removeTag(tag), tag))}</div>
          </div>
        </form>
      </Dialog>
    </Page>
  );
};

export default Templates;
